import { BOOKING_ID, CLICKOUT_SUBSECTIONS, ESTAB_FALLBACK_IMAGES, ESTABLISHMENT_TYPES_WITH_NO_REQUIRED_LICENSE, PARTNERS_WITHOUT_AVAILABILITY_CLICKOUT } from 'constants/constants';
import Carrousel from 'atoms/carrousel';
import { establishmentImagesInterface } from 'constants/interfaces';
import EstablishmentMap from 'templates/establishment/establishment-map';
import { getCancellationPolicyLiteral } from 'services/cancellation';
import { getDeterministicImage } from 'helpers/image-helper';
import Grid from 'app/atoms/grid';
import GridMatchingComponent from 'molecules/grid-matching-component';
import { handleGridClick } from 'templates/establishment/commons';
import ImageWithFallback from 'atoms/image-with-fallback';
import PageEstablishmentCta from 'molecules/page-establishment-cta';
import PageEstablishmentHundredRating from 'molecules/page-establishment-hundredrating';
import PageEstablishmentInfo from 'molecules/page-establishment-info';
import PageEstablishmentInfoNew from 'molecules/page-establishment-info-new';
import PageEstablishmentInline from 'molecules/page-establishment-inline';
import PageEstablishmentSection from 'molecules/page-establishment-section';
import PropTypes from 'prop-types';
import React from 'react';
import Scroll from 'react-scroll';
import Services from 'atoms/services';
import { servicesLiteral } from 'constants/literals';
import ShareFavourites from 'atoms/sharing-favourites';
import SnippetSimilar from 'molecules/snippet-similar';
import StickyBar from 'templates/establishment/sticky-bar';

const SEO_IMAGES = 4;
const CANCELLATION_LOGOS = {
  FULL: '/img/cancellation/full_cancellation_icon.svg',
  GENERIC: '/img/cancellation/generic_cancellation_icon.svg',
  PARTIAL: '/img/cancellation/partial_cancellation_icon.svg',
};

function chooseCancellationLogo(has100PercentCancellationPolicy, cancellationPolicies) {
  const hasPartialCancellationPolicy = Object.keys(cancellationPolicies).length > 1;
  if (has100PercentCancellationPolicy && hasPartialCancellationPolicy) return CANCELLATION_LOGOS.GENERIC;
  if (has100PercentCancellationPolicy) return CANCELLATION_LOGOS.FULL;
  return CANCELLATION_LOGOS.PARTIAL;
}

function getCancellationPolicyDetails(cancellationPolicies, t) {
  const sortedPolicies = Object.keys(cancellationPolicies)
    .map(policy => Number(policy))
    .sort((a, b) => a < b)
    .reduce((acc, policyRefund) => {
      const refundType = policyRefund === 100 ? 'full' : 'partial';
      acc[refundType].push({ [policyRefund]: cancellationPolicies[policyRefund] });
      return acc;
    }, { full: [], partial: [] });

  return Object.keys(sortedPolicies).map((cancellationType) => {
    if (!sortedPolicies[cancellationType].length) return null;
    return (
      <div key={ `cancellation_policy_type_${cancellationType}` } className="page-establishment-cta__text">
        <div className="page-establishment-cta__text__title">
          { cancellationType === 'full' ? t("Free cancellation") : t('Partial Cancellation') }
        </div>
        <div className="page-establishment-cta__text__subtitle">
          { sortedPolicies[cancellationType].map((policy, index) => (
            <p key={ `cancellation_policy_${cancellationType}_${index}` } className="cancellation_policy__refund_text">
              { getCancellationPolicyLiteral(policy, t) }
            </p>
          ))
          }
        </div>
      </div>
    );
  });
}

const noNeedLicense = (category, partners) => partners.some(partner => partner.id === BOOKING_ID) || ESTABLISHMENT_TYPES_WITH_NO_REQUIRED_LICENSE.includes(category);

export default function PageEstablishment({ adjective, area, areImagesLoaded, arrival, breadcrumb,
  category, categoryLiteral, city, clickoutSubsections, combinedRating, countryCode, currentSlide,
  description, discount, domainCulture, features, handleClickSimilar, handleCopyClick, handleEvents, handleFacebookShareClick,
  handleLeadClick, handleSaveClick, handleWhatsappClick, hasBigPartnersOffers, hasCancellationPolicy, id, images,
  isCookieBannerShowing, isCopied, isIE, isMounted, isPhone, isProcessing, isSaved, isTablet, latitude, leaving,
  licence, longitude, maxDayPrice, maxGuests, minDayPrice, numberFormat, numberOfComments, numberOfNights,
  partners, partnersRating, partnersWithMultiLogo, partnersWithoutAvailabilityClickout, partnerWithLead,
  ranking, recommendation, rooms, searchGuests, similars, stars, t, title, titlePreposition }) {

  const menuItems = [{
    anchor: description ? '#establishment-page-description' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-description') : null,
    name: t("Information"),
  }, {
    anchor: latitude && longitude ? '#establishment-page-map' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-map') : null,
    name: t("Location"),
  }, {
    anchor: features && features.length > 0 ? '#establishment-page__services' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page__services') : null,
    name: t("Services"),
  }, {
    anchor: partnerWithLead && partnerWithLead.pricePerNight && partnerWithLead.priceTotal ? '#establishment-page-lead' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-lead') : null,
    name: t("Contact"),
  }, {
    anchor: !isIE && combinedRating && combinedRating > 5 ? '#establishment-page-hundred-rating' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-hundred-rating') : null,
    name: t("Rating"),
  }, {
    anchor: partners && partners.length > 1 ? '#establishment-page__grid-matching' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page__grid-matching') : null,
    name: t("Offers"),
  }, {
    anchor: hasCancellationPolicy && partners[0].cancellationPolicy && Object.keys(partners[0].cancellationPolicy).length !== 0 ? '#establishment-page-cancellation' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-cancellation') : null,
    name: t("Cancellation"),
  }, {
    anchor: similars && similars.length ? '#establishment-page-similars' : null,
    element: typeof document !== 'undefined' ? document.querySelector('#establishment-page-similars') : null,
    name: t("Similars"),
  }];

  const listener = () => {
    const element = document.getElementById('contentMenu');
    const offsetFix = 682;
    if (!element) return;
    if (offsetFix < window.pageYOffset) element.classList.add('fixed');
    else element.classList.remove('fixed');
    menuItems.map((item, i) => {
      if (!item.element) return false;
      const topLimit = item.element.id === 'establishment-page-description' ? item.element.offsetTop - 120 : item.element.offsetTop - 45;
      const bottomLimit = item.element.offsetTop + item.element.offsetHeight - 10;
      if (topLimit <= window.pageYOffset && bottomLimit >= window.pageYOffset
          && document.getElementById(`item${i}`)) {
        document.getElementById(`item${i}`).classList.add('selected');
      } else if (document.getElementById(`item${i}`)) {
        document.getElementById(`item${i}`).classList.remove('selected');
      }
      return false;
    });
  };

  const showButton = partners.length
    && !(
      !partners[0].pricePerNight
      && !partners[0].priceTotal
      && PARTNERS_WITHOUT_AVAILABILITY_CLICKOUT.includes(partners[0].id)
    );

  if (typeof window !== 'undefined' && 'classList' in document.documentElement) {
    window.addEventListener('scroll', listener, false);
  }

  const handleScroll = (element, configuration = { delay: 0, duration: 500, smooth: true }) => {
    if (!element) return;
    if (element === '#establishment-page-description') {
      Scroll.animateScroll.scrollTo(document.querySelector(element).offsetTop - 120, configuration);
    } else {
      Scroll.animateScroll.scrollTo(document.querySelector(element).offsetTop + 20, configuration);
    }
  };

  let imagesArray = [];
  let altInServerFormated = '';
  if (breadcrumb && images) {
    const imgsInServer = images.slice(0, 4);
    const imgsInClient = images.slice(4);

    let imgsInClientFormated = '';
    const imgsInServerFormated = imgsInServer.map(img => img.medium);
    altInServerFormated = imgsInServer.map((img) => {
      let imgAlt = null;
      if (img.alt) imgAlt = img.alt.replace(/[\t\n\r]/gm, '');
      return imgAlt;
    });

    if (imgsInClient.length) imgsInClientFormated = imgsInClient.map(img => img.small);
    imagesArray = imgsInServerFormated.concat(imgsInClientFormated);
  }
  const imagesToCarr = breadcrumb ? imagesArray : images;
  const ComponentPageEstablishmentInfo = hasBigPartnersOffers ? PageEstablishmentInfoNew : PageEstablishmentInfo;

  return (
    <div className="establishment-page">
      <div className="establishment-page__header">
        <Grid
          breadcrumb={ !!breadcrumb }
          className="establishment-page__header__grid"
          estabFallbackImages={ ESTAB_FALLBACK_IMAGES }
          getFallbackSrc={ getDeterministicImage }
          handleclick={ handleGridClick }
          id={ id }
          images={ images }
          isIE={ isIE }
          partners={ partners }
          showButton={ !!showButton }
          t={ t }
        />
        <div className="establishment-page__header__favourite" onClick={ handleSaveClick }>
          { !breadcrumb && <span className={ `establishment-card__top__favourite__icon ${isSaved ? 'hundredicons-hr-icon-favorito-filled' : 'hundredicons-hr-icon-favorito'}` } /> }
        </div>

        <Carrousel
          className="establishment-page__header__carrousel"
          currentSlide={ currentSlide }
          isSwipeEnabled={ true }
          showArrows={ !breadcrumb ? areImagesLoaded : true }
          showBullets={ false }
          showSpinner={ true }
        >
          { Array.from(Array(breadcrumb ? SEO_IMAGES : imagesToCarr.length), (x, i) => (
            <ImageWithFallback
              key={ i }
              alt={ breadcrumb && i < SEO_IMAGES ? altInServerFormated[i] : null }
              className={ `carrousel__image ${breadcrumb ? '-isImgTag' : ''}` }
              fallbackSrc={ getDeterministicImage(id, ESTAB_FALLBACK_IMAGES, i) }
              showAsBackground={ !breadcrumb }
              src={ imagesToCarr[i] }
            />
          )) }
        </Carrousel>
        <ComponentPageEstablishmentInfo
          allPartners={ partners }
          area={ area }
          arrival={ arrival }
          breadcrumb={ breadcrumb }
          categoryLiteral={ categoryLiteral }
          city={ city }
          className="establishment-page__header__info"
          clickoutSubsections={ clickoutSubsections }
          combinedRating={ combinedRating }
          countryCode={ countryCode }
          discount={ discount }
          handleEvents={ handleEvents }
          handleScroll={ handleScroll }
          hasCancellationPolicy={ hasCancellationPolicy }
          instantBook={ !!partners.length && partners[0].instantlyBooking }
          leaving={ leaving }
          maxDayPrice={ maxDayPrice }
          maxGuests={ maxGuests }
          minDayPrice={ minDayPrice }
          numberFormat={ numberFormat }
          numberOfComments={ numberOfComments }
          numberOfNights={ numberOfNights }
          partnersWithMultiLogo={ partnersWithMultiLogo }
          partnersWithoutAvailabilityClickout={ partnersWithoutAvailabilityClickout }
          ranking={ ranking }
          recommendation={ recommendation }
          rooms={ rooms }
          searchGuests={ searchGuests }
          stars={ stars }
          t={ t }
          title={ title }
          titlePreposition={ titlePreposition }
        />
      </div>
      { !isPhone && !breadcrumb
      && (
        <PageEstablishmentInline
          className="establishment-page__header__inline"
          handleCopyCick={ handleCopyClick }
          handleFaceBookCick={ handleFacebookShareClick }
          handleSaveCick={ handleSaveClick }
          image="/img/page-establishment/img-info.png"
          isCopied={ isCopied }
          isProcessing={ isProcessing }
          isSaved={ isSaved }
          quote={ t('Best price guaranteed for this holiday rental by comparing over 100 sites in Hundredrooms.') }
          t={ t }
        />
      )
      }
      { (isPhone || isTablet) && !breadcrumb
        && (
          <ShareFavourites
            baseClassName="establishment-page__sharing-favourites"
            handleCopyClick={ handleCopyClick }
            handleFacebookShareClick={ handleFacebookShareClick }
            handleSaveClick={ handleSaveClick }
            handleWhatsappClick={ handleWhatsappClick }
            isCopied={ isCopied }
            isProcessing={ isProcessing }
            isSaved={ isSaved }
            t={ t }
          />
        )
      }
      <div className="establishment-page__content">
        <div className="establishment-page__content__left-side">
          <ul className="establishment-page__content__left-side__menu" id="contentMenu">
            { menuItems.map((item, i) => (item.anchor
              ? (
                <li
                  key={ `item${i}` }
                  className="establishment-page__content__left-side__menu__item"
                  id={ `item${i}` }
                  onClick={ () => handleScroll(item.anchor) }
                >
                  { item.name }
                </li>
              )
              : '')) }
          </ul>
        </div>
        <div className="establishment-page__content__right-side">
          { !!description
          && (
            <div className="establishment-page__content__right-side__establishment-description" id="establishment-page-description">
              <h2 className="establishment-page__content__right-side__establishment-description__title">
                { t('Info about the establishment') }
              </h2>
              <p className="establishment-page__content__right-side__establishment-description__info">
                { description.length > 600 && !breadcrumb ? `${description.slice(0, 600)}...` : description }
              </p>
              <p className="establishment-page__content__right-side__establishment-description__info">
                { t('Licence number:') }
                {' '}
                { licence || (noNeedLicense(category, partners) ? t('Not required') : t('Not available')) }
              </p>
              { description.length > 600 && !breadcrumb
            && partners.length
                ? (
                  <span
                    className="establishment-page__content__right-side__establishment-description__read-more"
                    onClick={ partners[0].pricePerNight === null && partners[0].priceTotal === null && PARTNERS_WITHOUT_AVAILABILITY_CLICKOUT.includes(partners[0].id)
                      ? ''
                      : partners[0].handleClickout(CLICKOUT_SUBSECTIONS.ESTABLISHMENT_READ_MORE)
                    }
                  >
                    { t('Read more on {partner}', { partner: partners[0].visibleName }) }
                  </span>
                ) : null }
            </div>
          ) }
          { (latitude && longitude)
            && (
              <div className="establishment-page__content__right-side__map" id="establishment-page-map">
                <div className="establishment-page__content__right-side__separator" />
                <EstablishmentMap
                  isMounted={ isMounted }
                  latitude={ latitude }
                  longitude={ longitude }
                  t={ t }
                />
              </div>
            )
          }
          { partnerWithLead && partnerWithLead.pricePerNight && partnerWithLead.priceTotal
            && (
              <div className="establishment-page__content__right-side__lead" id="establishment-page-lead">
                <div className="establishment-page__content__right-side__separator" />
                <PageEstablishmentCta
                  buttonText={ t('Contact now') }
                  className="establishment-page__content__right-side__cta"
                  handleOnClick={ handleLeadClick }
                  image="/img/page-establishment/img-keys.png"
                  subtitle={ t('Contact the owner of this accommodation without commitment, and solve any doubts you may have.') }
                  title={ t('Contact the owner') }
                />
              </div>
            )
          }
          { features && features.length > 0
            && (
              <div id="establishment-page__services">
                <div className="establishment-page__content__right-side__separator" />
                <Services
                  baseClassName="establishment-page__content__right-side__services__component"
                  serviceIds={ features }
                  servicesLiteral={ servicesLiteral }
                  t={ t }
                />
              </div>
            )
          }
          { !isIE && combinedRating && combinedRating > 5 && combinedRating <= 10
            ? (
              <div id="establishment-page-hundred-rating">
                <div className="establishment-page__content__right-side__separator" />
                <PageEstablishmentHundredRating
                  adjective={ adjective }
                  hundredRating={ combinedRating }
                  isPhone={ isPhone }
                  isTablet={ isTablet }
                  partners={ partners }
                  partnersRating={ partnersRating }
                  t={ t }
                />
              </div>
            ) : null
          }
          { partners.length > 1 && (
            <div className="establishment-page__content__right-side__grid-matching" id="establishment-page__grid-matching">
              <div className="establishment-page__content__right-side__separator" />
              <div className="establishment-page__content__right-side__grid-matching__title">
                { t('Comparison between booking sites') }
              </div>
              <GridMatchingComponent
                country={ countryCode }
                handleOnClick={ handleEvents }
                partners={ partners }
                partnersRating={ partnersRating }
                t={ t }
              />
            </div>)
          }
          { partners[0] && partners[0].cancellationPolicy && Object.keys(partners[0].cancellationPolicy).length !== 0 && hasCancellationPolicy
            && (
              <div className="establishment-page__content__right-side__cancellation" id="establishment-page-cancellation">
                <div className="establishment-page__content__right-side__separator" />
                <h2 className="establishment-page__content__right-side__cancellation__title">
                  { t('Cancellation') }
                </h2>
                <PageEstablishmentSection
                  className="establishment-page__content__right-side__cancellation"
                  image={ chooseCancellationLogo(hasCancellationPolicy.has100PercentCancellationPolicy, partners[0].cancellationPolicy) }
                >
                  <div>
                    { getCancellationPolicyDetails(partners[0].cancellationPolicy, t) }
                    { partners[0].cancellationFee
                    && (
                      <div className="page-establishment-cta__text">
                        <p className="establishment-page__content__right-side__establishment-description__note">
                          { t('{partner} can charge an additional fee when cancelling the reservation.', { partner: partners[0].visibleName }) }
                        </p>
                      </div>
                    )
                    }
                    <div className="page-establishment-cta__text">
                      <a
                        className="establishment-page__content__right-side__establishment-description__read-more"
                        onClick={ partners[0].handleClickout(CLICKOUT_SUBSECTIONS.ESTABLISHMENT_CANCELLATION) }
                      >
                        { t('View Cancellation policies on {partner}', { partner: partners[0].visibleName }) }
                      </a>
                    </div>
                  </div>
                </PageEstablishmentSection>
              </div>
            )
          }
          <div className="establishment-page__content__right-side__separator" />
          { (similars && similars.length > 0)
            && (
              <div className="establishment-page__content__right-side__similars" id="establishment-page-similars">
                <span className="establishment-page__content__right-side__similars__title">
                  { `${t('Similar establishments')}` }
                </span>
                <div className="establishment-page__content__right-side__similars__snippet">
                  { similars.map((similar, i) => (
                    <SnippetSimilar
                      key={ `similars__snippet__${i}` }
                      className="establishment-page__content__right-side__similars__snippet__item"
                      domainCulture={ domainCulture }
                      estabFallbackImages={ ESTAB_FALLBACK_IMAGES }
                      establishment={ similar }
                      handleOnClick={ handleClickSimilar }
                      numberFormat={ numberFormat }
                      position={ i + 1 }
                      searchGuests={ searchGuests }
                      showTotal={ 7 }
                      similarInPageEstablishment={ true }
                      t={ t }
                    />
                  )) }
                </div>
              </div>
            )
          }
        </div>
      </div>
      { partners[0]
        && (
          <StickyBar
            area={ area }
            arrival={ arrival }
            breadcrumb={ breadcrumb }
            categoryLiteral={ categoryLiteral }
            city={ city }
            clickoutSubsections={ clickoutSubsections }
            handleSaveClick={ handleSaveClick }
            instantBook={ partners.length && partners[0].instantlyBooking }
            isCookieBannerShowing={ isCookieBannerShowing }
            isSaved={ isSaved }
            leaving={ leaving }
            mainPartner={ partners[0] }
            numberFormat={ numberFormat }
            t={ t }
            title={ title }
            titlePreposition={ titlePreposition }
          />
        )
      }
    </div>
  );
}


PageEstablishment.defaultProps = {
  adjective: '',
  area: 0,
  areImagesLoaded: true,
  breadcrumb: null,
  category: null,
  categoryLiteral: '',
  city: '',
  clickoutSubsections: {},
  combinedRating: 0,
  countryCode: null,
  description: '',
  discount: 0,
  features: [],
  handleClickSimilar: () => {},
  handleEvents: () => {},
  handleLeadClick: () => {},
  hasBigPartnersOffers: true,
  isCookieBannerShowing: false,
  licence: '',
  maxDayPrice: null,
  maxGuests: 1,
  minDayPrice: null,
  numberFormat: n => n,
  numberOfComments: 0,
  numberOfNights: 1,
  partnersRating: {},
  partnersWithMultiLogo: {},
  partnersWithoutAvailabilityClickout: [],
  partnerWithLead: {},
  ranking: 0,
  rooms: 1,
  searchGuests: 2,
  similars: [],
  stars: null,
  title: '',
  titlePreposition: '',
};

PageEstablishment.propTypes = {
  adjective: PropTypes.string,
  area: PropTypes.number,
  areImagesLoaded: PropTypes.bool,
  arrival: PropTypes.string.isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    url: PropTypes.string,
  })),
  category: PropTypes.number,
  categoryLiteral: PropTypes.string,
  city: PropTypes.string,
  clickoutSubsections: PropTypes.shape(),
  combinedRating: PropTypes.number,
  countryCode: PropTypes.string,
  currentSlide: PropTypes.number.isRequired,
  description: PropTypes.string,
  discount: PropTypes.number,
  domainCulture: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.number),
  handleClickSimilar: PropTypes.func,
  handleCopyClick: PropTypes.func.isRequired,
  handleEvents: PropTypes.func,
  handleFacebookShareClick: PropTypes.func.isRequired,
  handleLeadClick: PropTypes.func,
  handleSaveClick: PropTypes.func.isRequired,
  handleWhatsappClick: PropTypes.func.isRequired,
  hasBigPartnersOffers: PropTypes.bool,
  hasCancellationPolicy: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  id: PropTypes.number.isRequired,
  images: PropTypes.arrayOf(establishmentImagesInterface).isRequired,
  isCookieBannerShowing: PropTypes.bool,
  isCopied: PropTypes.bool.isRequired,
  isIE: PropTypes.bool.isRequired,
  isMounted: PropTypes.bool.isRequired,
  isPhone: PropTypes.bool.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  isSaved: PropTypes.bool.isRequired,
  isTablet: PropTypes.bool.isRequired,
  latitude: PropTypes.number.isRequired,
  leaving: PropTypes.string.isRequired,
  licence: PropTypes.string,
  longitude: PropTypes.number.isRequired,
  maxDayPrice: PropTypes.number,
  maxGuests: PropTypes.number,
  minDayPrice: PropTypes.number,
  numberFormat: PropTypes.func,
  numberOfComments: PropTypes.number,
  numberOfNights: PropTypes.number,
  partners: PropTypes.arrayOf(PropTypes.shape({
    favicon: PropTypes.string.isRequired,
    handleClickout: PropTypes.func.isRequired,
    id: PropTypes.number.isRequired,
    pricePerNight: PropTypes.number,
    priceTotal: PropTypes.number,
    visibleName: PropTypes.string.isRequired,
  })).isRequired,
  partnersRating: PropTypes.shape(),
  partnersWithMultiLogo: PropTypes.shape(),
  partnersWithoutAvailabilityClickout: PropTypes.arrayOf(PropTypes.number),
  partnerWithLead: PropTypes.shape(),
  ranking: PropTypes.number,
  recommendation: PropTypes.bool.isRequired,
  rooms: PropTypes.number,
  searchGuests: PropTypes.number,
  similars: PropTypes.arrayOf(PropTypes.shape()),
  stars: PropTypes.number,
  t: PropTypes.func.isRequired,
  title: PropTypes.string,
  titlePreposition: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
